// src/components/stages/ConsultStage.tsx
// The doctor's consultation workspace, on the shared stage primitives — see
// docs/HMS_V4_UI_MODERNIZATION_HANDOFF.md §4 and the original wireframe's
// §03 for the container rebuild this file started from.
//
// Redesigned again 2026-09-17 (docs/HMS_V4_CONSULTATION_REDESIGN_PLAN.md
// Phase 1) to fix duplicated surfaces the first rebuild left behind: the
// allergy signal now lives only in PatientBanner (this file no longer renders
// its own AlertStrip); the rail leads with "Why today" instead of a
// Pre-Consult card, and consolidates Diagnoses/Prescription/Past history into
// one "This visit" card instead of three; and the approve/unfilled-field
// controls that used to live inside ConsultationChat's own footer now surface
// in this stage's one commit bar (via chatRef/chatReadiness), so they're
// visible regardless of which tab is active.
//
// Deliberately unchanged: ConsultationChat, PrescriptionBuilder and
// DiagnosisPanel keep every internal behaviour — the approve-before-save
// gate, forceMount state preservation across tab switches, and the
// prescription parsers. Only where they sit (and, for ConsultationChat, how
// its readiness is reported outward) changed.
//
// It embeds the structured clinical capture widgets (encounter diagnoses,
// prescriptions) that write to the tables from migration_clinical_capture.sql
// — see HMS_V4_ABDM_FHIR_READINESS_REPORT.md's hybrid-capture recommendation:
// those sit alongside the AI narrative chat, not in place of it.
import { useState, useEffect, useCallback, useRef, useMemo, type CSSProperties, type ReactNode } from 'react';
import { HeartPulse, Stethoscope, Lock, Home, BedDouble, AlertTriangle } from 'lucide-react';
import ConsultationChat, { type ConsultationApprovedPayload, type ConsultationChatHandle, type ConsultationReadiness } from '../ConsultationChat';
import type { AssessmentSummary } from '../AssessmentSummaryCard';
import { AssessmentSummaryRail } from './preconsult/AssessmentSummaryRail';
import { MedicalHistoryTabs } from '../../features/patients/MedicalHistoryTabs';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SegmentedControl } from '../ui/SegmentedControl';
import { StageActionBar } from '../ui/StageActionBar';
import { StageLayout, StageLayoutMain, StageLayoutRail } from '../ui/StageLayout';
import NewLabOrderDialog from '../../features/laboratory/NewLabOrderDialog';
import { ReferencesDrawer } from '../../features/clinical-references/ReferencesDrawer';
import { insertIntoNote } from '../../features/clinical-references/api';
import { HoldConsultationDialog } from '../../features/queue/HoldConsultationDialog';
import { holdConsultation } from '../../features/queue/api';
import { VitalTile } from '../ui/VitalTile';
import { checkBp, checkPulse, checkTemp, checkSpo2, checkBmi } from '@/lib/vitalThresholds';
import { useQueryClient } from '@tanstack/react-query';
import { patientDiagnoses, patientProcedures, patientMedications, generatePatientTimeline, resumeConsultation } from '../../api';
import { keys } from '@/lib/queryKeys';
import { useTodayQueue } from '../../features/queue/useTodayQueue';
import { loadConfig, getActiveModel } from '../../aiConfig';
import { DiagnosisPanel } from '../../features/consultation/DiagnosisPanel';
import { PreviousConsultationsCard } from '../../features/consultation/PreviousConsultations';
import { PrescriptionBuilder } from '../../features/consultation/PrescriptionBuilder';
import { useAllergies } from '../../features/consultation/useAllergies';
import { NO_KNOWN_ALLERGIES, useAllergyStatus } from '../../features/consultation/useAllergyStatus';
import { useVisitDiagnoses, usePatientDiagnoses, useCreateDiagnosis } from '../../features/consultation/useEncounterDiagnoses';
import { useVisitPrescriptions } from '../../features/consultation/usePrescriptionDraft';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { useAuth } from '../../hooks/useAuth';
import { AISummaryPanel } from '../../features/patients/AISummaryPanel';
import { WhyTodaySymptoms } from '../../features/symptom-form/WhyTodaySymptoms';
import { symptomsForConsultation } from '../../features/symptom-form/symptomForm';
import { useResizableWidth } from '../../hooks/useResizableWidth';

// The reference rail's resizable width — same drag mechanics and per-
// account persistence as ConsultationChat.tsx's note↔assistant split
// (useResizableWidth), a separate stored key since the two are independent
// preferences.
const RAIL_WIDTH_KEY = 'hms_consult_rail_width';
const DEFAULT_RAIL_WIDTH = 330;
const MIN_RAIL_WIDTH = 220;
const MAX_RAIL_WIDTH = 480;

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase());
}

// Flattens the ailment template's arbitrary JSONB answers (checkbox-group
// arrays, radio strings, vitals sub-objects, free text) into readable
// "Label: value" lines — used both for the doctor-visible summary card and
// the AI's hidden context, so neither loses what the nurse recorded.
function flattenAnswers(obj: Record<string, unknown> | null | undefined): string[] {
  return Object.entries(obj || {}).map(([key, value]) => {
    if (value === '' || value == null) return null;
    if (Array.isArray(value)) {
      return value.length ? `${humanizeKey(key)}: ${value.join(', ')}` : null;
    }
    if (typeof value === 'object') {
      const sub = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== '' && v != null)
        .map(([k, v]) => {
          // A due-checklist field's value is one level deeper still — each
          // item maps to { last_done }, not a primitive — so stringifying v
          // directly here is what used to print "HbA1c [object Object]".
          if (v && typeof v === 'object') {
            const lastDone = (v as { last_done?: string }).last_done;
            return lastDone ? `${k} (done ${new Date(lastDone).toLocaleDateString('en-IN')})` : `${k} (due)`;
          }
          return `${k} ${v}`;
        }).join(', ');
      return sub ? `${humanizeKey(key)}: ${sub}` : null;
    }
    return `${humanizeKey(key)}: ${value}`;
  }).filter((line): line is string => Boolean(line));
}

const DISPOSITIONS = [
  { value: 'OP' as const, label: <span className="inline-flex items-center whitespace-nowrap"><Home size={13} className="mr-1" />Outpatient (OP)</span> },
  { value: 'IP' as const, label: <span className="inline-flex items-center whitespace-nowrap"><BedDouble size={13} className="mr-1" />Inpatient (IP)</span> },
];
type Disposition = 'OP' | 'IP';

interface VisitStageRow {
  stage: string;
  stage_data: Record<string, any> | null;
}

interface ConsultVisit {
  id: number;
  /** Business-key visit code (VIS-...), used by the encounter-diagnoses API. */
  visit_id: string;
  assigned_doctor_id?: number | null;
  assigned_doctor_name?: string | null;
  stages?: VisitStageRow[];
}

interface ConsultPatient {
  id?: number;
  uhid?: string;
  name?: string;
  gender?: string;
  age?: number | null;
}

interface ConsultStageProps {
  visit: ConsultVisit;
  // Nullable to match the shared StageProps every other stage takes (see
  // types/visit.ts) — VisitWorkflow.tsx's own `patient` state is `Patient |
  // null` throughout the pipeline's brief pre-identity window, even though
  // by the time a visit reaches Consultation it is always set. Every access
  // below already goes through `patient?.` for exactly this reason.
  patient: ConsultPatient | null;
  completeStage: (stage: string, data: Record<string, unknown>) => void;
  loading: boolean;
  onViewPatientRecord?: (uhid: string) => void;
  reportPredictedStageSkips?: (stageIds: string[]) => void;
  /** Mirrors StageProps.onViewStage — `opts.edit` opens the stage modal
   * straight in its edit state (the Vitals card's "✎ Edit"). */
  onViewStage?: (stageId: string, opts?: { edit?: boolean }) => void;
}

// ── Rail chrome ──────────────────────────────────────────────────
//
// The rail's cards are deliberately lighter than the workspace's: a small
// uppercase heading on a plain card, no lettered index and no status badge.
// SectionCard's chrome is for a sequence someone has to work through
// (Pre-Consult's A/B/C); this column is reference material sitting beside
// the work, and giving it the same weight would make the two columns
// compete.
export function RailCard({ title, icon, tone = 'neutral', action, children }: {
  title: string;
  icon?: ReactNode;
  tone?: 'neutral' | 'vitals' | 'triage';
  action?: ReactNode;
  children: ReactNode;
}) {
  const toneClass = tone === 'vitals' ? 'text-success-ink' : tone === 'triage' ? 'text-warn-ink' : 'text-ink-muted';
  return (
    <div className="rounded-[var(--radius)] border border-line bg-card p-3">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${toneClass}`}>
          {icon}{title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function ConsultStage({ visit, patient, completeStage, loading, onViewPatientRecord, reportPredictedStageSkips, onViewStage }: ConsultStageProps) {
  const { user } = useAuth();
  const { width: railWidth, startResize: startRailResize } = useResizableWidth({
    key: RAIL_WIDTH_KEY, defaultValue: DEFAULT_RAIL_WIDTH, min: MIN_RAIL_WIDTH, max: MAX_RAIL_WIDTH,
  });
  // Full allergy rows, feeding the AI's extraContext below — react-query
  // dedupes this against PatientBanner's own useAllergies call (the source of
  // the banner's "Why?" popover, 1.1), so it isn't a second request.
  const allergiesQuery = useAllergies(patient?.uhid || '');
  const allergyStatus = useAllergyStatus(patient?.uhid || '');
  const diagnosesQuery = useVisitDiagnoses(visit?.visit_id || '');
  // "Repeat from last visit" chips (UX opportunity from the redesign review):
  // a chronic patient's diagnosis is often the same visit after visit, and
  // typing "Type 2 Diabetes Mellitus" out again every time is exactly the
  // kind of repetition this saves. patientDiagnosesQuery is already ordered
  // newest-first by visit date (clinicalApi.ts); the most recent row whose
  // visit_code differs from *this* visit is "last visit's" diagnoses.
  const patientDiagnosesQuery = usePatientDiagnoses(patient?.uhid);
  const createDiagnosisMutation = useCreateDiagnosis(visit?.visit_id || '', patient?.uhid);
  const lastVisitDiagnoses = (() => {
    const rows = patientDiagnosesQuery.data ?? [];
    const other = rows.find(d => d.visit_code && d.visit_code !== visit?.visit_id);
    if (!other) return [];
    const currentNames = new Set((diagnosesQuery.data ?? []).map(d => d.diagnosis.toLowerCase()));
    return rows
      .filter(d => d.visit_code === other.visit_code)
      .filter(d => !currentNames.has(d.diagnosis.toLowerCase()));
  })();
  const [addedCarryForwardDx, setAddedCarryForwardDx] = useState<Set<string>>(new Set());
  // 🧪 Lab order dialog — see the "Lab order" commit-bar button lower down.
  const [labOrderOpen, setLabOrderOpen] = useState(false);
  const [referencesOpen, setReferencesOpen] = useState(false); // 📚 Hospital references (G1)
  const [longHistory, setLongHistory] = useState<Array<{ diagnosis: string; year?: number }>>([]);
  const [procedureHistory, setProcedureHistory] = useState<Array<{ procedure: string; year?: number }>>([]);
  const [medicationHistory, setMedicationHistory] = useState<Array<{ drug_name: string; dose?: string; frequency?: string; active?: boolean }>>([]);
  // Feeds extraContext below (silently, to the AI) and the rail's history
  // counts (marker 5) — the same longitudinal history / past procedures /
  // current+past medications the History tab renders in full, fetched here in
  // plain list form since those panels own their fetch internally rather than
  // lifting it, and the AI needs plain text, not component state.
  const uhid = patient?.uhid;
  const loadHistory = useCallback(() => {
    if (!uhid) return;
    patientDiagnoses.list(uhid).then(setLongHistory).catch(() => {});
    patientProcedures.list(uhid).then(setProcedureHistory).catch(() => {});
    patientMedications.list(uhid).then(setMedicationHistory).catch(() => {});
  }, [uhid]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // History is normally captured at Pre-Consult. When it wasn't — including
  // every visit that skipped Pre-Consult ('None' at Vitals) — the doctor adds
  // it here: by hand in the History tab, or pulled from the patient's earlier
  // visits, consultations and uploaded documents by AI — the same
  // POST /timeline/generate the Pre-Consult stage uses, which seeds the three
  // structured history tables (rows tagged source='ai').
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [historyPulling, setHistoryPulling] = useState(false);
  const [historyPullError, setHistoryPullError] = useState('');
  const pullHistoryWithAi = async () => {
    if (!uhid) return;
    setHistoryPulling(true); setHistoryPullError('');
    try {
      const aiCfg = loadConfig();
      await generatePatientTimeline(uhid, aiCfg.provider, getActiveModel(aiCfg));
      loadHistory();
      setHistoryRefresh(n => n + 1);
    } catch (e) {
      setHistoryPullError(e instanceof Error ? e.message : String(e));
    } finally {
      setHistoryPulling(false);
    }
  };
  // Approving a document used to advance the visit to the next stage
  // immediately — no chance to actually read what was generated before it's
  // gone. Now approval just satisfies one line of the readiness statement;
  // the doctor moves on explicitly from the commit bar once they've checked
  // the document (still visible above, in the chat transcript).
  const [approvedPayload, setApprovedPayload] = useState<ConsultationApprovedPayload | null>(null);
  // The OP/IP decision now lives here instead of its own separate stage —
  // the doctor already has the full clinical picture in front of them, so
  // asking again on a fresh screen a click later added nothing.
  // stage_data.visit_type drives the backend's routing (straight to the IP
  // ward-admission stage, or to Pharmacy/Billing for OP — see advanceStage in
  // routes/visits.js).
  const [visitType, setVisitType] = useState<Disposition | null>(null);
  // Soft nudge, not a gate: a doctor could approve Consultation Notes and
  // disposition the visit having never opened the Prescription tab, and
  // nothing would catch a genuinely missed prescription — the pipeline only
  // ever checks "is there a prescription", never "did anyone decide". This
  // makes that a conscious choice instead of a silent default, without
  // blocking the (common, legitimate) medication-free visit.
  const [noRxConfirmed, setNoRxConfirmed] = useState(false);
  // Controlled so the rail's history card (marker 5) can open the History tab
  // (marker 8) — decision 3, option C: the rail answers "is there anything",
  // the tab answers "what".
  const [tab, setTab] = useState('notes');
  // 1.4 — the rail's "This visit" card expands DiagnosisPanel in place
  // instead of always showing it, same convention as AssessmentSummaryBar.
  const [diagnosesOpen, setDiagnosesOpen] = useState(false);
  // 1.3 — the approve/unfilled-field controls moved out of ConsultationChat's
  // own footer into this stage's one commit bar; readiness is reported back
  // via callback since the fields themselves live inside that component.
  const chatRef = useRef<ConsultationChatHandle>(null);
  const [chatReadiness, setChatReadiness] = useState<ConsultationReadiness>({
    hasDocument: false, approved: false, unfilledCount: 0, unfilledLabels: [],
    canRecord: false, recording: false,
  });
  // The queue row's LIVE status, not the one frozen into the queue stage's
  // stage_data. It matters now that a consultation can be 'On Hold': an
  // interrupted consultation must present itself as paused and be resumed by
  // an explicit action, not silently restarted because the doctor happened to
  // open the screen (see ConsultationChat's own start effect).
  const { data: todayQueueRows } = useTodayQueue(patient?.id);
  const [resumeError, setResumeError] = useState('');
  const [holdPrompt, setHoldPrompt] = useState<{ fromLab: boolean } | null>(null);
  const [holding, setHolding] = useState(false);
  const [holdError, setHoldError] = useState('');
  const [resuming, setResuming] = useState(false);
  const queryClient = useQueryClient();

  const stageEntryFor = (stageId: string) => visit?.stages?.find(s => s.stage === stageId) || null;
  const stageData = (stageId: string): Record<string, any> | null => stageEntryFor(stageId)?.stage_data || null;
  const vitals     = stageData('vitals');
  const preConsult = stageData('pre_consult');
  const queueData  = stageData('queue');

  if (!queueData?.queue_id) {
    return (
      <div className="card fade-in" style={{ maxWidth: 480, padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: '#DC2626' }}>This visit has no queue entry yet — go back to the Queue stage first.</div>
      </div>
    );
  }

  // Only the doctor this patient is actually assigned to (see queue.doctor_id
  // — the same assignment ConsultationModule's own patient list filters on)
  // can process the consultation. Backend enforces this too (see
  // PATCH /:visitId/stage in routes/visits.js) — this is just the friendlier
  // front door so a doctor doesn't type out a whole consult only to hit a
  // 403 on approval.
  if (visit?.assigned_doctor_id && user?.role !== 'Admin' && user?.id !== visit.assigned_doctor_id) {
    return (
      <div className="card fade-in" style={{ maxWidth: 480, padding: 24, textAlign: 'center' }}>
        <Lock size={26} style={{ margin: '0 auto 8px', color: '#6B7280' }} />
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>This patient is assigned to {visit.assigned_doctor_name || 'another doctor'}</div>
        <div style={{ fontSize: 12.5, color: '#6B7280' }}>You don't have edit access to this consultation.</div>
      </div>
    );
  }

  const liveQueueRow = todayQueueRows?.find(r => r.id === queueData.queue_id) ?? null;
  // Falls back to 'Waiting' when the queue list hasn't loaded (or this row
  // predates visit_id linking) — the same value this used to be hardcoded to,
  // so nothing about the normal path changes.
  const liveQueueStatus = liveQueueRow?.status ?? 'Waiting';
  const onHold = liveQueueStatus === 'On Hold';

  // Hold while waiting for something (lab results, a scan…) so the doctor can
  // see the next patient; asked automatically right after a lab order is
  // placed (`fromLab`). A parked hold drops to the end of the doctor's queue
  // and is never "next" (backend utils/queuePriority.js isParkedHold).
  const inConsultation = liveQueueStatus === 'In Consultation';
  const confirmHold = async (reason: string) => {
    if (!liveQueueRow) return;
    setHolding(true); setHoldError('');
    try {
      await holdConsultation(liveQueueRow.id, reason);
      setHoldPrompt(null);
      queryClient.invalidateQueries({ queryKey: keys.queue.today() });
    } catch (e) {
      setHoldError(e instanceof Error ? e.message : String(e));
      setHoldPrompt(null);
    } finally { setHolding(false); }
  };

  const resumeHeldConsultation = async () => {
    if (!liveQueueRow) return;
    setResuming(true); setResumeError('');
    try {
      await resumeConsultation(liveQueueRow.id);
      queryClient.invalidateQueries({ queryKey: keys.queue.today() });
    } catch (e) {
      setResumeError(e instanceof Error ? e.message : String(e));
    } finally { setResuming(false); }
  };

  const queueItem = {
    id: queueData.queue_id as number,
    patient_id: patient?.id,
    uhid: patient?.uhid,
    name: patient?.name || '',
    gender: patient?.gender,
    age: patient?.age,
    bp_systolic: vitals?.bp_sys,
    bp_diastolic: vitals?.bp_dia,
    pulse_rate: vitals?.pulse,
    temperature: vitals?.temp,
    spo2: vitals?.spo2,
    blood_group: vitals?.bloodGroup,
    weight_kg: vitals?.weight,
    height_cm: vitals?.height,
    bmi: vitals?.bmi,
    // Structured symptoms reach the chat and the AI as complaint + labelled
    // staff notes, not the one combined line (see symptomsForConsultation).
    symptoms: symptomsForConsultation(vitals) ?? vitals?.symptoms,
    status: liveQueueStatus,
  };

  const templateAnswerLines = flattenAnswers(preConsult?.template_answers);
  const templateSummary: AssessmentSummary | null = preConsult?.template_summary ?? null;
  const screeningsDue = (templateSummary?.due_checklist ?? []).filter(d => d.color !== 'green');

  // Everything the AI is given silently, on every generate-document /
  // prescription-safety call — deliberately broader than what's rendered on
  // screen. Pre-consult triage was the only source before; this now also
  // covers the structured allergy record, this visit's own diagnoses, and
  // the patient's longitudinal diagnosis/procedure/medication history (the
  // same data the History tab renders for the doctor, in plain-text form)
  // so a drafted document is grounded in the whole chart, not just what the
  // nurse typed at triage. See CLAUDE.md's "Always send real patient/vitals
  // objects" note — this is the equivalent discipline for history/allergies.
  const allergyLines = (allergiesQuery.data ?? [])
    .filter(a => a.substance !== NO_KNOWN_ALLERGIES && a.clinical_status === 'active')
    .map(a => `${a.substance}${a.criticality ? ` (${a.criticality})` : ''}${a.reaction ? ` — reaction: ${a.reaction}` : ''}`);
  const encounterDiagnosisLines = (diagnosesQuery.data ?? []).map(d => d.diagnosis);
  const longHistoryLines = longHistory.map(h => h.year ? `${h.diagnosis} (${h.year})` : h.diagnosis);
  const procedureLines = procedureHistory.map(p => p.year ? `${p.procedure} (${p.year})` : p.procedure);
  const currentMedLines = medicationHistory.filter(m => m.active !== false).map(m => [m.drug_name, m.dose, m.frequency].filter(Boolean).join(' '));
  const pastMedLines = medicationHistory.filter(m => m.active === false).map(m => [m.drug_name, m.dose].filter(Boolean).join(' '));

  const extraContext = [
    preConsult ? `Pre-consultation nurse triage — Ailment: ${preConsult.ailment || 'General'}, Risk level: ${preConsult.risk_level || '—'}, Pain scale: ${preConsult.pain_scale || '—'}/10` : null,
    preConsult?.allergies ? `Allergies (nurse-reported): ${preConsult.allergies}` : null,
    preConsult?.current_meds ? `Current medications (nurse-reported): ${preConsult.current_meds}` : null,
    preConsult?.additional_vitals ? `Additional observations: ${preConsult.additional_vitals}` : null,
    templateAnswerLines.length ? `Ailment-specific findings (${preConsult?.ailment}): ${templateAnswerLines.join('; ')}` : null,
    templateSummary?.overall ? `Overall assessment risk: ${templateSummary.overall}` : null,
    preConsult?.nurse_notes ? `Nurse notes: ${preConsult.nurse_notes}` : null,
    allergyLines.length ? `Known allergies (structured record): ${allergyLines.join('; ')}` : null,
    encounterDiagnosisLines.length ? `Diagnoses recorded this visit: ${encounterDiagnosisLines.join('; ')}` : null,
    longHistoryLines.length ? `Longitudinal diagnosis history: ${longHistoryLines.join('; ')}` : null,
    procedureLines.length ? `Past procedures: ${procedureLines.join('; ')}` : null,
    currentMedLines.length ? `Current medications (patient record): ${currentMedLines.join('; ')}` : null,
    pastMedLines.length ? `Past/discontinued medications: ${pastMedLines.join('; ')}` : null,
  ].filter(Boolean).join('\n');

  // Doctor-visible counterpart to extraContext above (which only feeds the
  // AI, silently) — rendered into ConsultationChat's own greeting message,
  // right after Chief complaints, so the full pre-consult picture is in the
  // doctor's actual reading flow instead of requiring a glance at the rail's
  // Pre-Consult card off to the side.
  const preConsultSummary = preConsult
    ? [
        // renderMsg only bolds a line that's *entirely* wrapped in `**` (see
        // ConsultationChat.tsx), so the ailment can't share this line with
        // the heading the way the rail's version does — it moves to the row below.
        '**Pre-Consult triage**',
        `${preConsult.ailment && preConsult.ailment !== 'General' ? `${preConsult.ailment} · ` : ''}Risk: ${preConsult.risk_level || '—'} · Pain: ${preConsult.pain_scale || '—'}/10`,
        preConsult.allergies ? `Allergies (nurse-reported): ${preConsult.allergies}` : null,
        preConsult.current_meds ? `Current medications: ${preConsult.current_meds}` : null,
        preConsult.additional_vitals ? `Additional observations: ${preConsult.additional_vitals}` : null,
        templateAnswerLines.length ? templateAnswerLines.join(' · ') : null,
        templateSummary?.overall ? `Overall assessment risk: ${templateSummary.overall}` : null,
        preConsult.nurse_notes ? `Nurse notes: "${preConsult.nurse_notes}"` : null,
      ].filter(Boolean).join('\n')
    : '';

  // consultation_id (returned by ConsultationChat's approve()) lets the
  // Pharmacy stage right after find exactly the prescription written in
  // *this* visit, rather than guessing from "today's approved Rx" for the patient.
  const handleApproved = (payload: ConsultationApprovedPayload) => setApprovedPayload(payload);
  const continueToNextStage = () => completeStage('consultation', { ...approvedPayload, approved: true, visit_type: visitType });

  const prescriptionsQuery = useVisitPrescriptions(visit.id);
  const prescriptions = prescriptionsQuery.data ?? [];
  const rxItemCount = prescriptions.reduce((n, p) => n + (p.item_count ?? 0), 0);
  const hasPrescription = rxItemCount > 0;
  const rxNudgeBlocking = !hasPrescription && !noRxConfirmed;

  // Same grant the Laboratory module's own "+ New order" button checks
  // (LabOrdersTab.tsx) — and the same one the server enforces on POST
  // /api/laboratory/orders. A doctor without it simply doesn't see the button.
  const canOrderLab = !!user?.modules?.includes('lab:orders') && !!patient?.id;
  // 📚 Hospital references (G1): answered for this patient, de-identified.
  // Either reference source opens the drawer; each tab checks its own grant.
  const canAskReferences = !!user?.modules?.some((m: string) => m === 'clinical-references' || m === 'clinical-references:web');

  // Memoised deliberately. NewLabOrderDialog re-seeds its whole form whenever
  // the patient prop's IDENTITY changes (its reset effect depends on it), so
  // a fresh object literal here would clear the doctor's ticked tests on any
  // incidental re-render of this screen — a background query settling, the
  // allergy status arriving — mid-order.
  const labPatient = useMemo(
    () => (patient?.id
      ? {
          id: patient.id,
          name: patient.name || '',
          uhid: patient.uhid || '',
          age: patient.age ?? null,
          gender: patient.gender || '',
        }
      : null),
    [patient?.id, patient?.name, patient?.uhid, patient?.age, patient?.gender]
  );

  const historyCount = longHistory.length + procedureHistory.length + medicationHistory.length;
  const findingCount = templateAnswerLines.length;

  // The readiness statement the commit bar carries (marker 9). Ordered by
  // what the doctor has to do next, so the bar always names exactly one
  // outstanding thing rather than listing everything at once.
  const blocker = !approvedPayload
    ? 'Approve the consultation note to continue'
    : !visitType
      ? 'Choose a disposition — OP or IP — to continue'
      : rxNudgeBlocking
        ? 'Confirm the prescription status to continue'
        : null;

  // Ctrl/Cmd+Enter drives the same two actions the commit bar's own primary
  // button does, in sequence — approve the note first, then (once approved
  // and a disposition is picked) continue the visit. This is the single
  // highest-frequency motion on this screen, run once per consultation all
  // day, and previously had no keyboard path at all — a doctor's hand had
  // to leave the keyboard for the mouse every time. Deliberately still fires
  // from inside the note editor (a contentEditable surface, and the thing
  // the doctor's cursor sits in most of the time) — Ctrl+Enter isn't a
  // newline there, so nothing about typing the note is disturbed. It's
  // skipped only for a plain INPUT/TEXTAREA (the chat message box), which
  // already sends on a bare Enter regardless of the Ctrl/Cmd state — letting
  // this fire there too would both send a chat message and approve/continue
  // on the same keypress.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey)) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (chatReadiness.hasDocument && !chatReadiness.approved && chatReadiness.unfilledCount === 0) {
        e.preventDefault();
        chatRef.current?.approve();
      } else if (!loading && !blocker) {
        e.preventDefault();
        continueToNextStage();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [chatReadiness, loading, blocker]); // eslint-disable-line react-hooks/exhaustive-deps

  // Publishes which later stages the doctor's in-progress disposition
  // already implies will be skipped, so VisitStepper's compact bar can grey
  // them out ahead of the real backend decision — see
  // types/visit.ts's own StageProps.reportPredictedStageSkips doc comment
  // and backend/routes/visits.js's advanceStage for the actual rules this
  // mirrors: OP skips IP; no structured prescription row skips Pharmacy; IP
  // skips Pharmacy *and* Billing (an admission settles at discharge
  // instead). Deliberately keyed off `hasPrescription`, not the
  // `noRxConfirmed` checkbox — the checkbox only gates *continuing*, it
  // isn't what the backend actually checks.
  useEffect(() => {
    if (!reportPredictedStageSkips) return;
    const skips: string[] = [];
    if (visitType === 'OP') {
      skips.push('ip');
      if (!hasPrescription) skips.push('pharmacy');
    } else if (visitType === 'IP') {
      skips.push('pharmacy', 'billing');
    }
    reportPredictedStageSkips(skips);
  }, [visitType, hasPrescription, reportPredictedStageSkips]);

  return (
    <div className="fade-in flex flex-col gap-3.5" style={{ minHeight: 460 }}>
      {/* Marker 2 — the allergy safety signal now lives entirely in the
          patient banner itself (PatientBanner.tsx's AllergyBadge + its "Why?"
          popover, 2026-09-17 redesign 1.1) — a separate AlertStrip here would
          be a second, potentially disagreeing surface for the same fact. */}

      {/* Phase 3 — the AI patient summary, compact, in the space the old
          AlertStrip used to occupy. Cached-only on mount (no AI call); never
          auto-generates. Same component Patient Records uses (variant="full"
          there) — see features/patients/AISummaryPanel.tsx. */}
      {patient?.uhid && <AISummaryPanel uhid={patient.uhid} variant="compact" />}

      {/* §5 — this consultation was interrupted (an emergency patient) and is
          waiting to be resumed. Everything already captured is still here and
          still editable context; what's paused is the doctor's claim on the
          patient, so the one thing this banner does is hand it back. */}
      {onHold && (
        <div className="mb-3 rounded-[var(--radius)] border border-warn bg-warn-soft px-3.5 py-3 text-[13px] text-warn-ink">
          <div className="font-bold">⏸ Consultation on hold</div>
          <div className="mt-1">
            {liveQueueRow?.hold_reason
              ? `Paused — ${liveQueueRow.hold_reason}.`
              : 'This consultation was paused.'}{' '}
            Nothing entered so far has been lost. Resume to continue from where you stopped.
            {liveQueueRow?.queue_parked && ' Meanwhile you can see your next patient from the Queue screen.'}
          </div>
          {liveQueueRow && liveQueueRow.lab_total > 0 && (
            <div className="mt-1 font-semibold">
              🧪 {liveQueueRow.lab_ready >= liveQueueRow.lab_total
                ? 'All lab results are ready.'
                : `${liveQueueRow.lab_ready} of ${liveQueueRow.lab_total} lab results ready.`}
            </div>
          )}
          {resumeError && <div className="mt-1 font-semibold text-danger">{resumeError}</div>}
          <Button size="sm" className="mt-2" loading={resuming} onClick={resumeHeldConsultation}>
            ▶ Resume consultation
          </Button>
        </div>
      )}

      {/* Marker 3 — a sticky reference rail beside the fluid workspace,
          stacking below lg. The rail is context the doctor consults; the
          workspace is the work itself. */}
      <StageLayout variant="split">
        {/* Resizable, same drag mechanics and per-account persistence as the
            Notes tab's note↔assistant split (ConsultationChat.tsx) — see
            useResizableWidth. @container so the Vitals grid below can react
            to this box's actual width rather than the viewport's, and
            correctly drop to one column instead of overlapping itself when
            dragged narrow (the same failure mode a fixed lg:w-[200px] had
            earlier in v3). w-full below lg: the rail stacks full-width
            there regardless of the stored pixel value — only the lg-prefixed
            --rail-w width class applies it (not spelled out here: Tailwind
            scans comments and would emit an invalid class for it), so a narrow phone/tablet view is never forced to this
            desktop preference. */}
        <div className="w-full shrink-0 @container lg:w-[var(--rail-w)]" style={{ '--rail-w': `${railWidth}px` } as CSSProperties}>
          <StageLayoutRail className="lg:w-full">
          {/* 1.4 — "Why today" leads the rail: the chief complaint is the
              first thing a doctor needs, ahead of vitals or history. Falls
              back to the triage ailment when Vitals recorded no symptoms
              text (e.g. a returning patient checked in without saying why). */}
          {(vitals?.symptoms || preConsult) && (
            <RailCard
              title="Why today"
              tone="triage"
              icon={<Stethoscope size={13} />}
              // Opens the same Pre-Consult StageEditModal the stepper's own
              // completed bubble opens — see StageProps.onViewStage's doc
              // comment. This used to toggle a raw templateAnswerLines dump
              // in place, which read as unstructured text; the modal already
              // presents the identical data (and lets a doctor correct it,
              // which the inline dump never could).
              action={findingCount > 0 && (
                <button type="button" onClick={() => onViewStage?.('pre_consult')} className="shrink-0 text-[11px] text-teal underline-offset-2 hover:underline">
                  ▸ All {findingCount}
                </button>
              )}
            >
              {/* Structured symptoms (Admin → Clinical → Vitals Form Customization)
                  as chips + label/value rows; older visits' free text clamped. */}
              <WhyTodaySymptoms
                vitals={vitals}
                fallback={preConsult?.ailment && preConsult.ailment !== 'General' ? preConsult.ailment : '—'}
              />
              {vitals?.symptoms && preConsult?.ailment && preConsult.ailment !== 'General' && (
                <div className="mt-0.5 text-[11px] font-semibold text-warn-ink">{preConsult.ailment}</div>
              )}
              {preConsult && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {preConsult.risk_level && (
                    <Badge variant={preConsult.risk_level === 'High' ? 'danger' : preConsult.risk_level === 'Medium' ? 'warn' : 'neutral'}>
                      Triage risk: {preConsult.risk_level}
                    </Badge>
                  )}
                  {preConsult.pain_scale != null && preConsult.pain_scale !== '' && (
                    <Badge variant="warn">Pain {preConsult.pain_scale}/10</Badge>
                  )}
                </div>
              )}
              {preConsult?.current_meds && <div className="mt-1.5 text-[12px] text-ink">Meds (nurse): {preConsult.current_meds}</div>}
              {screeningsDue.length > 0 && (
                <div className="mt-1 text-[12px] font-medium text-danger-ink">
                  {screeningsDue.length} screening{screeningsDue.length === 1 ? '' : 's'} due: {screeningsDue.map(d => d.label).join(', ')}
                </div>
              )}
            </RailCard>
          )}

          {vitals && (() => {
            const checks = [
              checkBp(vitals.bp_sys, vitals.bp_dia), checkPulse(vitals.pulse), checkTemp(vitals.temp),
              checkSpo2(vitals.spo2), checkBmi(vitals.bmi),
            ];
            const anyFlagged = checks.some(c => c.warn);
            return (
              <RailCard
                title="Vitals"
                tone="vitals"
                icon={<HeartPulse size={13} />}
                // "✎ Edit" opens the Vitals stage's own form (StageEditModal
                // -> VitalsStage) straight in its edit state, and saves via
                // the vitals-only amend endpoint. Correcting a mistyped
                // reading used to mean rolling the whole visit back to the
                // Vitals stage — which moves current_stage, re-opens the
                // stage for the nurse, drops the doctor out of Consultation
                // and releases the queue row. Nothing about that is implied
                // by "the BP is wrong". This edits the reading in place:
                // stage, queue position, assignment and consultation state
                // are all untouched (see visits.js's /amend, which writes
                // only stage_data and the linked vitals row).
                //
                // Sits beside, not instead of, the advisory-range note —
                // losing that line when a reading is correctable would be a
                // regression in the card's clinical meaning.
                action={
                  <div className="flex shrink-0 items-center gap-2">
                    {!anyFlagged && <span className="text-[11px] font-normal normal-case text-ink-muted">all within advisory range</span>}
                    <button
                      type="button"
                      onClick={() => onViewStage?.('vitals', { edit: true })}
                      className="shrink-0 text-[11px] font-semibold normal-case text-teal underline-offset-2 hover:underline"
                    >
                      ✎ Edit
                    </button>
                  </div>
                }
              >
                {/* Same content as before the rail narrowed for v3 (all six
                    vitals, not just flagged ones) — only the grid itself
                    changed, from 3 columns to 2, so tiles have room to
                    breathe at the rail's new 200px width instead of
                    overlapping. Same thresholds module every other screen
                    flags against — lib/vitalThresholds.ts. Before it existed
                    these ranges were copy-pasted into four screens and had
                    drifted. Corrections go through this card's own "✎ Edit"
                    above, which opens the one real Vitals form
                    (StageEditModal.tsx -> VitalsStage) — still not a second,
                    lookalike edit form defined here. */}
                {/* Container-width driven, not viewport-width — @container on
                    the rail wrapper above means this reacts to the RAIL's
                    own (user-resizable) box, not a screen breakpoint, so
                    dragging the rail narrow drops to one column instead of
                    overlapping itself (the same failure mode a fixed
                    lg:w-[200px] rail had earlier in v3). */}
                <div className="grid grid-cols-1 gap-x-2 gap-y-2 @[260px]:grid-cols-2">
                  <VitalTile icon="🫀" label="BP" value={vitals.bp_sys && vitals.bp_dia ? `${vitals.bp_sys}/${vitals.bp_dia}` : null} unit="mmHg" check={checks[0]} />
                  <VitalTile icon="💓" label="Pulse" value={vitals.pulse} unit="bpm" check={checks[1]} />
                  <VitalTile icon="🌡️" label="Temp" value={vitals.temp} unit="°F" check={checks[2]} />
                  <VitalTile icon="🫁" label="SpO₂" value={vitals.spo2} unit="%" check={checks[3]} />
                  <VitalTile icon="📊" label="BMI" value={vitals.bmi} check={checks[4]} />
                  <VitalTile icon="⚖️" label="Weight" value={vitals.weight} unit="kg" />
                </div>
              </RailCard>
            );
          })()}

          {/* 1.4 — "This visit" consolidates what used to be three separate
              cards (Diagnoses, Prescription's own summary never existed as a
              card, Past history) into one, each row a count + the one action
              that matters. Allergies are gone from this card entirely — the
              banner is the one allergy surface now (1.1). Structured capture
              (DiagnosisPanel) is unchanged internally, only expand-in-place
              here instead of always visible, same convention
              AssessmentSummaryRail already uses below. */}
          {patient?.uhid && (
            <RailCard title="This visit">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-[13px]">
                  <span className="flex-1 text-ink">
                    Diagnoses <span className="text-ink-muted">· {diagnosesQuery.data?.length ? diagnosesQuery.data.length : 'none yet'}</span>
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setDiagnosesOpen(v => !v)}>
                    {diagnosesOpen ? 'Hide' : '+ Add'}
                  </Button>
                </div>
                {lastVisitDiagnoses.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-ink-muted">🔁 From last visit:</span>
                    {lastVisitDiagnoses.map(d => {
                      const added = addedCarryForwardDx.has(d.diagnosis);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          disabled={added || createDiagnosisMutation.isPending}
                          onClick={() => {
                            setAddedCarryForwardDx(prev => new Set(prev).add(d.diagnosis));
                            createDiagnosisMutation.mutate({ diagnosis: d.diagnosis, clinical_status: 'active' });
                          }}
                          className="rounded-full border border-border px-2 py-0.5 text-[11px] text-ink hover:border-teal hover:text-teal disabled:cursor-default disabled:opacity-50 disabled:hover:border-border disabled:hover:text-ink-muted"
                        >
                          {added ? '✓ ' : '+ '}{d.diagnosis}
                        </button>
                      );
                    })}
                  </div>
                )}
                {diagnosesOpen && <DiagnosisPanel visitCode={visit.visit_id} uhid={patient.uhid} />}

                <div className="flex items-center gap-2 text-[13px]">
                  <span className="flex-1 text-ink">
                    Prescription <span className="text-ink-muted">· {rxItemCount > 0 ? `${rxItemCount} item${rxItemCount === 1 ? '' : 's'}` : 'nothing added'}</span>
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setTab('prescription')}>Open</Button>
                </div>

                {/* Marker 5 — longitudinal history, counts + the one action
                    that matters. Opening it switches to the History tab,
                    where the panels get the workspace's full width. */}
                {/* Was "· 0 · 0 · 0" — three bare numbers with no legend, so
                    reading it meant already knowing the convention. Named
                    counts (title attribute carries the full breakdown too,
                    for a quick hover check) cost a little width but nothing
                    to decode. */}
                {historyCount === 0 ? (
                  <div className="flex items-center gap-2 text-[13px]">
                    <span className="flex-1 text-ink-muted">Past history · none recorded</span>
                  </div>
                ) : (
                  <button type="button" onClick={() => setTab('history')} className="text-left text-[13px] text-ink hover:text-teal"
                    title={`${longHistory.length} condition${longHistory.length === 1 ? '' : 's'} · ${procedureHistory.length} procedure${procedureHistory.length === 1 ? '' : 's'} · ${medicationHistory.length} medication${medicationHistory.length === 1 ? '' : 's'}`}>
                    Past history <span className="text-ink-muted">· {longHistory.length} condition{longHistory.length === 1 ? '' : 's'} · {procedureHistory.length} procedure{procedureHistory.length === 1 ? '' : 's'} · {medicationHistory.length} med{medicationHistory.length === 1 ? '' : 's'}</span>
                  </button>
                )}
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="ghost" loading={historyPulling} onClick={pullHistoryWithAi}>🤖 Pull from records</Button>
                  {historyCount === 0 && <Button size="sm" variant="ghost" onClick={() => setTab('history')}>+ Add manually</Button>}
                </div>
                {historyPulling && <div className="text-[11px] text-ink-muted">Reading earlier visits and documents…</div>}
                {historyPullError && <div className="text-[11px] text-danger">{historyPullError}</div>}
              </div>
            </RailCard>
          )}

          {/* Previous consultation notes — a link per document, opening the
              full note in a popup, so the doctor can check what was written
              last time without leaving this screen. */}
          <PreviousConsultationsCard patientId={patient?.id} excludeQueueId={queueItem.id} />
          </StageLayoutRail>
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the reference rail"
          onMouseDown={startRailResize}
          className="hidden w-3 shrink-0 cursor-col-resize items-center justify-center lg:flex lg:self-stretch"
        >
          <div className="h-10 w-1 rounded-full bg-border" />
        </div>

        <StageLayoutMain>
          {/* Marker 7 — the same collapsed-tile/expand-to-full-card summary
              the nurse saw live at the bottom of Pre-Consult
              (AssessmentSummaryRail.tsx), reused here rather than a
              bespoke lookalike, so the doctor reads the identical
              "Automatic ... assessment summary" — same data, same shape —
              right above the tabs instead of a plain due-screenings badge.
              Not sticky here: this sits inline in the scrolling workspace,
              not pinned to the viewport like Pre-Consult's own footer bar. */}
          {templateSummary && (
            <AssessmentSummaryRail summary={templateSummary} title={`Automatic ${preConsult?.ailment || 'assessment'} summary`} sticky={false} />
          )}

          {/* Marker 8 — tabs, chat and prescription builder untouched
              internally. Only the container moved, plus the third History tab
              decision 3 asked for. */}
          <Tabs value={tab} onValueChange={setTab}>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
              <TabsList>
                <TabsTrigger value="notes">Consultation Notes</TabsTrigger>
                <TabsTrigger value="prescription">
                  Prescription{rxItemCount > 0 ? ` (${rxItemCount})` : ''}
                </TabsTrigger>
                <TabsTrigger value="history">
                  History{historyCount > 0 ? ` (${historyCount})` : ''}
                </TabsTrigger>
              </TabsList>

              {/* Contextual reminder right at the point of prescribing — same
                  status the top banner already shows, repeated here in the
                  otherwise-empty space next to the tabs (a doctor could be
                  sitting on the Prescription tab without ever having
                  scrolled past the banner above). Full text, not just a
                  dot — there's easily enough width in this row for it. The
                  "Record conversation" button (moved out of ConsultationChat's
                  own header — see ConsultationChatHandle.toggleRecording /
                  ConsultationReadiness above — so recording doesn't cost the
                  Notes panel a row of its own vertical space) shares this
                  same right-aligned group, shown only while the Notes tab's
                  own doc/mode state allows it (chatReadiness.canRecord). */}
              <div className="ml-auto flex items-center gap-2.5">
                {(allergyStatus === 'danger' || allergyStatus === 'warn') && (
                  <div className={`flex items-center gap-1.5 text-[12px] font-medium ${allergyStatus === 'danger' ? 'text-danger' : 'text-warn-ink'}`}>
                    <AlertTriangle size={13} className="shrink-0" />
                    {allergyStatus === 'danger' ? 'Check allergies before prescribing' : 'Allergies not yet checked — confirm before prescribing'}
                  </div>
                )}
                {canAskReferences && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setReferencesOpen(true)}>
                    📚 References
                  </Button>
                )}
                {canOrderLab && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setLabOrderOpen(true)}>
                    🧪 Lab order
                  </Button>
                )}
                {/* "Record conversation" moved into the Notes tab's own
                    assistant-column composer (v3, ConsultationComposer.tsx),
                    next to "Enter to send" — no longer a tab-row button. */}
              </div>
            </div>
            {/* forceMount + hide-when-inactive instead of Radix's default
                unmount-on-inactive: ConsultationChat holds a lot of local
                state (picked doc type, in-progress draft, voice recording)
                that a doctor switching to the Prescription tab and back
                should never lose. Unmounting also re-ran its "mark consult
                started" effect and re-fetched the transcript on every
                switch — wasteful even ignoring the state loss. */}
            <TabsContent value="notes" forceMount className="data-[state=inactive]:hidden">
              <div className="min-h-[480px]">
                <ConsultationChat
                  key={queueItem.id}
                  ref={chatRef}
                  queueItem={queueItem}
                  extraContext={extraContext}
                  preConsultSummary={preConsultSummary}
                  patientUhid={patient?.uhid}
                  visitId={visit.id}
                  visitCode={visit.visit_id}
                  onViewPatientRecord={onViewPatientRecord}
                  onApproved={handleApproved}
                  onReadinessChange={setChatReadiness}
                />
              </div>
            </TabsContent>
            <TabsContent value="prescription" forceMount className="data-[state=inactive]:hidden">
              {patient?.id && (
                <PrescriptionBuilder
                  patientId={patient.id}
                  visitId={visit.id}
                  uhid={patient.uhid}
                  visitCode={visit.visit_id}
                  aiClinicalContext={extraContext}
                  patientName={patient.name}
                  patientAge={patient.age}
                  patientGender={patient.gender}
                  vitals={queueItem}
                />
              )}
            </TabsContent>
            {/* Not forceMount: the history panels own their own fetch and
                hold no editing state worth preserving, so mounting them on
                first open is the cheaper behaviour. Editable, so history
                missed at (or skipped with) Pre-Consult can still be recorded here; edits refresh the
                rail counts and the AI's context. */}
            <TabsContent value="history">
              {patient?.uhid && (
                <div className="flex flex-col gap-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[12px] text-ink-muted">
                      {historyCount === 0 ? 'No history recorded yet — add it below, or pull it from earlier records.' : 'Add or correct history below.'}
                    </span>
                    <Button size="sm" variant="ghost" loading={historyPulling} onClick={pullHistoryWithAi}>
                      🤖 {historyCount === 0 ? 'Pull from records via AI' : 'Refresh from records via AI'}
                    </Button>
                    {historyPullError && <span className="text-[12px] text-danger">{historyPullError}</span>}
                  </div>
                  {/* One reusable sub-tabbed module instead of three
                      side-by-side compact columns — the columns read as a
                      cramped data table on anything narrower than a wide
                      desktop, and this is the same module Patient Records'
                      own history tab now uses (features/patients/
                      MedicalHistoryTabs.tsx), so both screens present a
                      doctor's diagnosis/procedure/medication history
                      identically. */}
                  <MedicalHistoryTabs uhid={patient.uhid} editable refreshToken={historyRefresh} onChanged={loadHistory} />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </StageLayoutMain>
      </StageLayout>

      {canAskReferences && (
        <ReferencesDrawer
          open={referencesOpen}
          onOpenChange={setReferencesOpen}
          visitId={visit.id}
          patientLabel={patient?.name ?? null}
          onInsert={insertIntoNote}
        />
      )}
      {canOrderLab && (
        <NewLabOrderDialog
          open={labOrderOpen}
          onClose={() => setLabOrderOpen(false)}
          patient={labPatient}
          visitId={visit.id}
          // Lab ordered mid-consultation: offer to park this patient while
          // the results come back, so the doctor can see the next one.
          onCreated={() => { if (inConsultation) setHoldPrompt({ fromLab: true }); }}
        />
      )}

      <HoldConsultationDialog
        open={!!holdPrompt}
        patientName={patient?.name || 'this patient'}
        intro={holdPrompt?.fromLab ? 'Lab order placed.' : undefined}
        cancelLabel={holdPrompt?.fromLab ? 'Keep consulting' : 'Cancel'}
        pending={holding}
        onCancel={() => setHoldPrompt(null)}
        onConfirm={confirmHold}
      />

      {/* Marker 9 — the commit bar is always present, disabled with the
          reason stated. Before this the whole block was hidden until the
          document was approved, so the gate was invisible rather than
          legible: the doctor could not see that a disposition would be asked
          for at all. Same gate, stated. */}
      <StageActionBar
        readiness={blocker ?? '✓ Ready to continue'}
        readinessTone={blocker ? 'warn' : 'success'}
      >
        {/* Pause this patient (waiting for results) and see the next one. */}
        {inConsultation && (
          <Button size="sm" variant="ghost" title="Put on hold while waiting for lab results, a scan…"
            onClick={() => setHoldPrompt({ fromLab: false })}>
            ⏸ Hold
          </Button>
        )}
        {holdError && <span className="text-[12px] text-danger">{holdError}</span>}
        {/* 1.3 — the approve/unfilled-field controls used to live inside
            ConsultationChat's own footer, invisible while the doctor was on
            the Prescription/History tab. They're driven from here now via
            chatRef/chatReadiness so they show regardless of which tab is
            active — fields left · Next field · Remove unfilled · Approve
            note, ahead of the disposition controls that were already here. */}
        {chatReadiness.hasDocument && !chatReadiness.approved && chatReadiness.unfilledCount > 0 && (
          <>
            <span className="text-[12.5px] font-medium text-warn-ink">
              {chatReadiness.unfilledCount} field{chatReadiness.unfilledCount === 1 ? '' : 's'} to fill
            </span>
            <Button
              size="sm" variant="ghost"
              onClick={() => { setTab('notes'); setTimeout(() => chatRef.current?.focusNextField(), 0); }}
            >
              Next field →
            </Button>
            <Button size="sm" variant="ghost" onClick={() => chatRef.current?.removeUnfilled()}>Remove unfilled</Button>
          </>
        )}
        {chatReadiness.hasDocument && !chatReadiness.approved && (chatReadiness.missingSections?.length ?? 0) > 0 && (
          <span className="text-[12.5px] text-warn-ink" title="The hospital's six-section clinical draft">
            Sections missing: {chatReadiness.missingSections!.join(', ')}
          </span>
        )}
        {chatReadiness.hasDocument && !chatReadiness.approved && (
          <Button
            size="sm" variant="success"
            disabled={chatReadiness.unfilledCount > 0}
            onClick={() => chatRef.current?.approve()}
            title="Ctrl+Enter"
          >
            ✓ Approve note
          </Button>
        )}
        {/* `shrink-0` + a `max-w` wide enough for "Outpatient (OP)"/"Inpatient
            (IP)" on one line each — without it this sits in the same flex row
            as the checkbox and the Continue button, which lets the row's
            default flex-shrink squeeze the track below its label's own
            content width and wrap "Outpatient (OP)" onto two lines. */}
        <SegmentedControl<Disposition>
          options={DISPOSITIONS}
          value={visitType}
          onChange={setVisitType}
          size="sm"
          className="w-auto max-w-none shrink-0"
        />
        {/* Not a hard gate — a medication-free visit is common and
            legitimate. This just stops "nobody opened the Prescription tab"
            from reading identically to "doctor decided no medication was
            needed", by making the second one something the doctor has to
            actually tick. */}
        {!hasPrescription && (
          <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-warn-ink">
            {/* Legacy global CSS (`input, select, textarea { width: 100% }` in
                index.css) targets every input indiscriminately, including
                checkboxes — without an explicit width override here, this
                checkbox stretches to fill the row and squeezes the label
                text into a sliver. */}
            <input type="checkbox" checked={noRxConfirmed} onChange={e => setNoRxConfirmed(e.target.checked)} style={{ width: 15, height: 15, flexShrink: 0 }} />
            No prescription needed
          </label>
        )}
        <Button onClick={continueToNextStage} disabled={loading || !!blocker} title="Ctrl+Enter">
          {loading ? 'Continuing…' : visitType ? `Continue as ${visitType} →` : 'Continue →'}
        </Button>
      </StageActionBar>
    </div>
  );
}
